import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../lib/axios'
import clsx from 'clsx'

const TYPE_META = {
  document:      { icon: '📄', label: 'Document',      color: 'text-blue-400' },
  specification: { icon: '📋', label: 'Specification',  color: 'text-purple-400' },
  test_plan:     { icon: '🧪', label: 'Test Plan',      color: 'text-amber-400' },
}

/**
 * GlobalSearch
 * Command-palette style full-text search across all documents.
 * Opens with Cmd+K / Ctrl+K.
 *
 * Props: onClose — () => void
 */
export default function GlobalSearch({ onClose }) {
  const [query, setQuery]     = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState(0)
  const inputRef  = useRef(null)
  const navigate  = useNavigate()
  const debounce  = useRef(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  const search = useCallback(async (q) => {
    if (q.trim().length < 2) { setResults([]); return }
    setLoading(true)
    try {
      const { data } = await api.get('/search', { params: { q } })
      setResults(data.results ?? [])
      setSelected(0)
    } catch {
      setResults([])
    } finally {
      setLoading(false)
    }
  }, [])

  const handleChange = (e) => {
    const q = e.target.value
    setQuery(q)
    clearTimeout(debounce.current)
    debounce.current = setTimeout(() => search(q), 300)
  }

  const navigate_to = (result) => {
    navigate(result.url)
    onClose()
  }

  const handleKey = (e) => {
    if (e.key === 'Escape') return onClose()
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(s => Math.min(s + 1, results.length - 1)) }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setSelected(s => Math.max(s - 1, 0)) }
    if (e.key === 'Enter' && results[selected]) navigate_to(results[selected])
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}>
      <div
        className="w-full max-w-2xl bg-cosmic-900 border border-white/12 rounded-2xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/8">
          <svg className="w-4 h-4 text-white/30 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleChange}
            onKeyDown={handleKey}
            placeholder="Search documents, specifications, test plans…"
            className="flex-1 bg-transparent text-white placeholder-white/25 outline-none text-sm"
          />
          {loading && <div className="w-4 h-4 border border-purple-400/30 border-t-purple-400 rounded-full animate-spin flex-shrink-0" />}
          <kbd className="text-[9px] text-white/20 bg-white/6 border border-white/10 px-1.5 py-0.5 rounded">ESC</kbd>
        </div>

        {/* Results */}
        <div className="max-h-[420px] overflow-y-auto">
          {results.length === 0 && query.length >= 2 && !loading && (
            <div className="px-4 py-8 text-center text-sm text-white/25">
              No results for "{query}"
            </div>
          )}

          {results.length === 0 && query.length < 2 && (
            <div className="px-4 py-8 text-center text-sm text-white/20">
              Type to search across all your documents
            </div>
          )}

          {results.map((result, i) => {
            const meta = TYPE_META[result.type] ?? TYPE_META.document
            return (
              <button
                key={result.id}
                type="button"
                onClick={() => navigate_to(result)}
                onMouseEnter={() => setSelected(i)}
                className={clsx(
                  'w-full text-left px-4 py-3 flex items-start gap-3 transition-colors',
                  i === selected ? 'bg-purple-500/10' : 'hover:bg-white/4',
                  i < results.length - 1 && 'border-b border-white/5'
                )}
              >
                <span className="text-xl flex-shrink-0 mt-0.5">{meta.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-semibold text-white truncate">{result.title}</span>
                    <span className={clsx('text-[9px] font-bold uppercase tracking-wide flex-shrink-0', meta.color)}>
                      {meta.label}
                    </span>
                  </div>
                  {result.excerpt && (
                    <p className="text-xs text-white/35 line-clamp-1">{result.excerpt}</p>
                  )}
                </div>
                <span className={clsx('text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 mt-0.5',
                  result.status === 'complete' ? 'text-emerald-400 bg-emerald-500/10' : 'text-blue-400 bg-blue-500/10')}>
                  {result.status}
                </span>
              </button>
            )
          })}
        </div>

        {/* Footer hints */}
        {results.length > 0 && (
          <div className="px-4 py-2 border-t border-white/6 flex items-center gap-4 text-[10px] text-white/20">
            <span><kbd className="bg-white/6 px-1 rounded">↑↓</kbd> Navigate</span>
            <span><kbd className="bg-white/6 px-1 rounded">↵</kbd> Open</span>
            <span><kbd className="bg-white/6 px-1 rounded">ESC</kbd> Close</span>
            <span className="ml-auto">{results.length} result{results.length !== 1 ? 's' : ''}</span>
          </div>
        )}
      </div>
    </div>
  )
}
